import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Paper, Chip, IconButton, Tooltip, InputAdornment, CircularProgress } from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Sidebar from '../landingPage/Sidebar';
import { PageHeader } from '../../components/ui/header';
import { PrimaryTextField } from '../../components/ui/input';
import { FilterButtons } from '../../components/ui/filter';
import { fetchAllTemplates } from '../../apis/templateAPI';
import { getDisplayNameFromConnector } from '../../utils/connectorUtils';

// Import image icons
import searchbarIcon from './imageIcons/SearchbarIcon.png';
import filterButtonIcon from './imageIcons/filterBtuttonIcon.png';
import salesforceIcon from './imageIcons/SalesforceIcons.png';
import blueDBIcon from './imageIcons/BlueDBIcon.png';
import greenDBIcon from './imageIcons/GreenDBIcon.png';
import csvFileIcon from './imageIcons/csvFileIcon.png';
import middleIcon from './imageIcons/MiddleIcon.png';
import previewIcon from './imageIcons/PreviewIcon.png';

// Helper function to map API response to component format
const mapApiTemplateToComponent = (apiTemplate) => {
  // Map connector types to source types
  const getSourceType = (connector) => {
    const connectorLower = (connector || '').toLowerCase();
    if (connectorLower.includes('salesforce') || connectorLower.includes('api') || connectorLower.includes('rest')) {
      return 'SaaS';
    }
    if (connectorLower.includes('csv') || connectorLower.includes('file')) {
      return 'File';
    }
    return 'Database';
  };

  // Map connector types to icon types
  const getIconType = (connector) => {
    const connectorLower = (connector || '').toLowerCase();
    if (connectorLower.includes('salesforce') || connectorLower.includes('api') || connectorLower.includes('rest')) {
      return 'cloud';
    }
    if (connectorLower.includes('csv') || connectorLower.includes('file')) {
      return 'file';
    }
    return 'database';
  };

  // Determine complexity based on entity and field mapping counts
  const getComplexity = (entityCount, fieldMappingCount) => {
    const total = (entityCount || 0) + (fieldMappingCount || 0);
    if (total <= 10) return 'Simple';
    if (total <= 25) return 'Medium';
    return 'Advanced';
  };

  // Determine if template is popular
  const isPopularTemplate = () => {
    const sourceConnector = (apiTemplate.source_connector || '').toLowerCase();
    const destinationConnector = (apiTemplate.destination_connector || '').toLowerCase();
    
    // Salesforce to Salesforce
    if (sourceConnector === 'salesforce' && destinationConnector === 'salesforce') {
      return true;
    }
    
    // MSSQL to Salesforce
    if (sourceConnector === 'salesforce' && (destinationConnector === 'mssql' || destinationConnector === 'sql')) {
      return true;
    }
    
    return false;
  };

  // Get display names using shared utility (matches ETLWorkflow.jsx)
  const sourceDisplayName = getDisplayNameFromConnector(apiTemplate.source_connector);
  const destinationDisplayName = getDisplayNameFromConnector(apiTemplate.destination_connector);

  return {
    id: apiTemplate.template_id,
    title: apiTemplate.name || '',
    description: apiTemplate.description || '',
    sourceType: getSourceType(apiTemplate.source_connector), // For filtering (SaaS, Database, File)
    sourceDisplayName: sourceDisplayName, // For display (Salesforce, SQL, HubSpot, Oracle)
    destinationDisplayName: destinationDisplayName, // For display (Salesforce, SQL, HubSpot, Oracle)
    complexity: getComplexity(apiTemplate.entity_count, apiTemplate.field_mapping_count),
    sourceIcon: getIconType(apiTemplate.source_connector),
    destinationIcon: getIconType(apiTemplate.destination_connector),
    usageCount: 0, // API doesn't provide usage count, defaulting to 0
    isPopular: isPopularTemplate(), // Set to true for Salesforce to Salesforce and MSSQL to Salesforce
    // Store original API data for detail page
    apiData: apiTemplate,
  };
};

const SOURCE_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'SaaS', label: 'SaaS' },
  { value: 'Database', label: 'Database' },
  { value: 'File', label: 'File' },
];

const COMPLEXITY_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'Simple', label: 'Simple' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Advanced', label: 'Advanced' },
];

const getIcon = (iconType, isDestination = false) => {
  switch (iconType) {
    case 'cloud':
      return (
        <Box
          component="img"
          src={salesforceIcon}
          alt="Source"
          sx={{
            width: { xs: 20, sm: 22, md: 24 },
            height: { xs: 20, sm: 22, md: 24 },
            objectFit: 'contain',
          }}
        />
      );
    case 'database':
      return (
        <Box
          component="img"
          src={isDestination ? greenDBIcon : blueDBIcon}
          alt={isDestination ? 'Destination' : 'Source'}
          sx={{
            width: { xs: 20, sm: 22, md: 24 },
            height: { xs: 20, sm: 22, md: 24 },
            objectFit: 'contain',
          }}
        />
      );
    case 'file':
      return (
        <Box
          component="img"
          src={csvFileIcon}
          alt="File"
          sx={{
            width: { xs: 20, sm: 22, md: 24 },
            height: { xs: 20, sm: 22, md: 24 },
            objectFit: 'contain',
          }}
        />
      );
    default:
      return (
        <Box
          component="img"
          src={salesforceIcon}
          alt="Source"
          sx={{
            width: { xs: 20, sm: 22, md: 24 },
            height: { xs: 20, sm: 22, md: 24 },
            objectFit: 'contain',
          }}
        />
      );
  }
};

const getTagColor = (type) => {
  switch (type) {
    case 'SaaS':
    case 'Database':
    case 'File':
      return '#ECEEF2'; // Light grey for all source types
    case 'Simple':
      return 'rgb(186, 246, 208)'; // Green for simple
    case 'Medium':
      return 'rgb(250, 211, 159)'; // Orange/brown for medium
    case 'Advanced':
      return '#FEE2E2'; // Light red for advanced
    default:
      return '#ECEEF2';
  }
};

const getTagTextColor = (type) => {
  switch (type) {
    case 'SaaS':
    case 'Database':
    case 'File':
      return '#374151'; // Dark grey text for source types
    case 'Simple':
      return '#047857'; // Dark green text
    case 'Medium':
      return '#B45309'; // Dark orange/brown text
    case 'Advanced':
      return '#991B1B'; // Dark red text
    default:
      return '#374151';
  }
};

const PreMadeTemplates = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSourceType, setSelectedSourceType] = useState('all');
  const [selectedComplexity, setSelectedComplexity] = useState('all');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch templates from API
  useEffect(() => {
    const loadTemplates = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {};
        
        // Map source type filter to connector type if needed
        if (selectedSourceType !== 'all') {
          // Note: API uses connector names, not source types
          // We'll filter client-side for now
        }
        
        // Add search parameter
        if (searchQuery) {
          params.search = searchQuery;
        }

        const response = await fetchAllTemplates(params);
        const mappedTemplates = response.templates.map(mapApiTemplateToComponent);
        setTemplates(mappedTemplates);
      } catch (err) {
        console.error('Error fetching templates:', err);
        setError(err.response?.data?.detail || 'Failed to load templates');
        toast.error(err.response?.data?.detail || 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [searchQuery]); // Only refetch when search changes

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const matchesSearch =
        searchQuery === '' ||
        template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.sourceType.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesSourceType =
        selectedSourceType === 'all' || template.sourceType === selectedSourceType;
      
      const matchesComplexity =
        selectedComplexity === 'all' || template.complexity === selectedComplexity;

      return matchesSearch && matchesSourceType && matchesComplexity;
    });
  }, [templates, searchQuery, selectedSourceType, selectedComplexity]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#FAFBFD' }}>
      <Sidebar />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <PageHeader
          title="Pre-Made Templates"
          showConnectorIcon={false}
          showSearchIcon={false}
        />

        <Box sx={{ flexGrow: 1, px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2 } }}>
          {/* Search Bar and Filters Button Row */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1.5, sm: 2 },
              alignItems: 'center',
              mb: { xs: 2, sm: 2.5, md: 3 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PrimaryTextField
                fullWidth
                placeholder="Search templates by name, source, or destination..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: { xs: 1, sm: 1.25 }, mr: 0 }}>
                      <Box
                        component="img"
                        src={searchbarIcon}
                        alt="Search"
                        sx={{
                          width: { xs: 18, sm: 20, md: 20 },
                          height: { xs: 18, sm: 20, md: 20 },
                          objectFit: 'contain',
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    backgroundColor: '#F3F3F5',
                    border: '1px solid #E5E7EB',
                    borderRadius: '4px',
                    boxShadow: 'none',
                    transition: 'all 0.2s ease',
                    '& .MuiInputBase-input': {
                      paddingLeft: { xs: '8px', sm: '10px' },
                      paddingRight: { xs: '12px', sm: '16px' },
                      '&::placeholder': {
                        color: '#717182',
                        opacity: 1,
                        fontFamily: 'IBM Plex Sans, sans-serif',
                      },
                    },
                    '&:hover': {
                      borderColor: '#D1D5DB',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                    },
                    '&.Mui-focused': {
                      borderColor: '#E5E7EB',
                      boxShadow: 'none',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #E5E7EB',
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid #E5E7EB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D1D5DB',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                  },
                }}
              />
            </Box>
            <Tooltip title="More Filters">
              <IconButton
                sx={{
                  border: '1px solid rgb(193, 193, 193)',
                  borderRadius: '4px',
                  px: { xs: 2, sm: 2.5, md: 3 },
                  height: '40px',
                  color: '#0F172A',
                  bgcolor: '#FFFFFF',
                  '&:hover': {
                    bgcolor: '#F9FAFB',
                    borderColor: '#0F172A',
                  },
                }}
              >
                <Box
                  component="img"
                  src={filterButtonIcon}
                  alt="Filter"
                  sx={{
                    width: { xs: 18, sm: 20, md: 22 },
                    height: { xs: 18, sm: 20, md: 22 },
                    objectFit: 'contain',
                    mr: { xs: 0.5, sm: 0.75 },
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 500,
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.9375rem' },
                    textTransform: 'none',
                  }}
                >
                  Filters
                </Typography>
              </IconButton>
            </Tooltip>
          </Box>

          {/* Filter Options */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 3, md: 4 },
              alignItems: { xs: 'stretch', sm: 'flex-start' },
              mb: { xs: 2, sm: 2.5, md: 3 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  color: '#3F3F3F',
                  mb: { xs: 1, sm: 1.25 },
                }}
              >
                Source Type:
              </Typography>
              <FilterButtons
                options={SOURCE_TYPE_OPTIONS}
                selectedValue={selectedSourceType}
                onChange={setSelectedSourceType}
                sx={{ mb: 0 }}
                buttonSx={{
                  height: '30px',
                  borderRadius: '4px',
                  border: '1px solid rgb(193, 193, 193)',
                  py: 0,
                  fontSize: { xs: '0.7rem', sm: '0.8125rem', md: '0.875rem' },
                }}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  color: '#3F3F3F',
                  mb: { xs: 1, sm: 1.25 },
                }}
              >
                Complexity:
              </Typography>
              <FilterButtons
                options={COMPLEXITY_OPTIONS}
                selectedValue={selectedComplexity}
                onChange={setSelectedComplexity}
                sx={{ mb: 0 }}
                buttonSx={{
                  height: '30px',
                  borderRadius: '4px',
                  border: '1px solid rgb(193, 193, 193)',
                  py: 0,
                  fontSize: { xs: '0.7rem', sm: '0.8125rem', md: '0.875rem' },
                }}
              />
            </Box>
          </Box>

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && !loading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  color: '#EF4444',
                }}
              >
                {error}
              </Typography>
            </Box>
          )}

          {/* Template Count */}
          {!loading && !error && (
            <Typography
              sx={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                fontWeight: 400,
                fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                color: '#3F3F3F',
                mb: { xs: 2, sm: 2.5, md: 3 },
              }}
            >
              {filteredTemplates.length} templates found
            </Typography>
          )}

          {/* Template Cards Grid */}
          {!loading && !error && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: { xs: 2, sm: 2.5, md: 3 },
              }}
            >
              {filteredTemplates.map((template) => {
                // Use template_id from API response, fallback to id
                const templateId = template.apiData?.template_id || template.id;
                return (
                  <Paper
                key={template.id}
                elevation={0}
                onClick={() => navigate(`/pre-made-templates/${templateId}`)}
                sx={{
                  p: { xs: 1.5, sm: 2, md: 2.5 },
                  borderRadius: '4px',
                  bgcolor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 1.25, sm: 1.5, md: 2 },
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {/* Popular Tag */}
                {template.isPopular && (
                  <Chip
                    icon={<StarIcon sx={{ fontSize: 16, color: '#FFF' }} />}
                    label="Popular"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: { xs: 12, sm: 14, md: 16 },
                      right: { xs: 12, sm: 14, md: 16 },
                      bgcolor: '#F0B100',
                      color: '#FFFFFF',
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontWeight: 500,
                      fontSize: { xs: '0.625rem', sm: '0.6875rem', md: '0.75rem' },
                      height: { xs: 20, sm: 22, md: 24 },
                      '& .MuiChip-icon': {
                        color: '#FFF',
                      },
                    }}
                  />
                )}

                {/* Icons Section */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 1.25, md: 1.5 },
                    mb: { xs: 0.5, sm: 0.75 },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 40, sm: 44, md: 48 },
                      height: { xs: 40, sm: 44, md: 48 },
                      borderRadius: '4px',
                      bgcolor: '#F1F5FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {getIcon(template.sourceIcon, false)}
                  </Box>
                  <Box
                    component="img"
                    src={middleIcon}
                    alt="Arrow"
                    sx={{
                      width: { xs: 18, sm: 20, md: 22 },
                      height: { xs: 18, sm: 20, md: 22 },
                      objectFit: 'contain',
                    }}
                  />
                  <Box
                    sx={{
                      width: { xs: 40, sm: 44, md: 48 },
                      height: { xs: 40, sm: 44, md: 48 },
                      borderRadius: '4px',
                      bgcolor: '#F1F5FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {getIcon(template.destinationIcon, true)}
                  </Box>
                </Box>

                {/* Title */}
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
                    lineHeight: 1.2,
                    color: '#000000',
                    mt: { xs: 0.5, sm: 0.75 },
                  }}
                >
                  {template.title}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                    lineHeight: 1.4,
                    color: '#525252',
                    flex: 1,
                  }}
                >
                  {template.description}
                </Typography>

                {/* Tags */}
                        <Box
                          sx={{
                            display: 'flex',
                            gap: { xs: 0.75, sm: 1 },
                            flexWrap: 'wrap',
                            mb: { xs: 0.5, sm: 0.75 },
                          }}
                        >
                          <Chip
                            label={template.sourceDisplayName || template.sourceType}
                            size="small"
                            sx={{
                              bgcolor: getTagColor(template.sourceType),
                              color: getTagTextColor(template.sourceType),
                              fontFamily: 'IBM Plex Sans, sans-serif',
                              fontWeight: 500,
                              fontSize: { xs: '0.625rem', sm: '0.6875rem', md: '0.75rem' },
                              height: { xs: 20, sm: 22, md: 24 },
                              px: { xs: 0.75, sm: 1 },
                              borderRadius: '4px',
                              border: 'none',
                            }}
                          />
                  <Chip
                    label={template.complexity}
                    size="small"
                    sx={{
                      bgcolor: getTagColor(template.complexity),
                      color: getTagTextColor(template.complexity),
                      border: `1px solid ${getTagTextColor(template.complexity)}`,
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontWeight: 500,
                      fontSize: { xs: '0.625rem', sm: '0.6875rem', md: '0.75rem' },
                      height: { xs: 20, sm: 22, md: 24 },
                      px: { xs: 0.75, sm: 1 },
                      borderRadius: '4px',
                    }}
                  />
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: { xs: 0.5, sm: 0.75 },
                    borderTop: '1px solid #E5E7EB',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                      color: '#6B7280',
                    }}
                  >
                    {template.usageCount.toLocaleString()} uses
                  </Typography>
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/pre-made-templates/${templateId}`);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.7,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'IBM Plex Sans, sans-serif',
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                        color: '#0F172A',
                        '&:hover': {
                          color: '#3C9564',
                        },
                      }}
                    >
                      Preview
                    </Typography>
                    <Box
                      component="img"
                      src={previewIcon}
                      alt="Preview"
                      sx={{
                        width: { xs: 14, sm: 16, md: 16 },
                        height: { xs: 14, sm: 16, md: 16 },
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PreMadeTemplates;
